import axios from 'axios'
import type {
  Choice,
  WealthConfig,
  SimulationResult,
  SuggestedVariable,
  AgentSynthesis,
} from './types'
import { analytics } from './lib/analytics'

const BACKEND = import.meta.env.VITE_BACKEND_URL || ''
const API = `${BACKEND}/api`

const client = axios.create({ baseURL: API, timeout: 60000, withCredentials: true })

// On 401, attempt a silent token refresh once, then redirect to login.
// Promise-queue pattern: all concurrent 401s wait for one refresh, then replay together.
let _refreshPromise: Promise<void> | null = null

client.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status
    const url: string = err?.config?.url ?? ''
    // Don't retry auth endpoints themselves, and replay any request at most
    // once: a 401 that survives a refresh (e.g. /simulate on a decision this
    // session doesn't own) must surface, not loop refresh → replay → 401.
    if (status === 401 && !url.includes('/auth/') && !err.config?._retried) {
      if (!_refreshPromise) {
        _refreshPromise = client.post('/auth/refresh').then(
          () => { _refreshPromise = null },
          () => {
            _refreshPromise = null
            // Don't redirect from public pages — avoids bouncing an anonymous or
            // expired-session visitor to /login when a background call (FxBootstrap,
            // PreferencesSync, …) gets a 401 on a page that doesn't require auth.
            // Entries are prefixes: '/blog' also covers '/blog/:slug', etc. Keep this
            // list in sync with the public routes in App.tsx.
            const publicPaths = [
              '/login', '/signup', '/forgot-password', '/reset-password',
              '/share', '/explore', '/how-it-works', '/how-to', '/pricing', '/compare',
              '/methodology', '/about', '/support', '/privacy', '/terms',
              '/blog', '/decisions', '/should-i-quit', '/',
              // One-tap outcome page from the check-in email: works signed out.
              '/checkin',
              // Local-first pages: fully usable anonymously (decisions live in
              // localStorage; /api/simulate is optional-auth). A background 401
              // here must never bounce the user off their own results.
              '/results', '/decision', '/app',
            ]
            const onPublicPage = publicPaths.some(p => window.location.pathname === p || window.location.pathname.startsWith(p + '/'))
            if (!onPublicPage) {
              window.location.href = '/login'
            }
            // Reject so waiters skip the replay — without this the failed
            // refresh resolved and every waiter replayed into the same 401.
            throw new Error('session refresh failed')
          }
        )
      }
      try {
        await _refreshPromise
        return client({ ...err.config, _retried: true }) // replay once after refresh
      } catch {
        return Promise.reject(err)
      }
    }
    return Promise.reject(err)
  },
)

export async function runSimulation(payload: {
  decision_id: string
  decision_name: string
  horizon_years: number
  discount_rate: number
  risk_aversion: number
  wellbeing_value?: number | null
  n_simulations: number
  seed?: number
  wealth?: WealthConfig
  choices: Choice[]
  // What-if: computed but not stored, so the decision's forecast of record
  // (what a check-in commits and outcomes are scored on) stays put.
  preview?: boolean
}): Promise<SimulationResult> {
  // backend expects variables (not nested model)
  const body = {
    ...payload,
    // null (legacy synced decisions) must be omitted, not sent: the backend
    // field is a plain float with a default and would reject null.
    wellbeing_value: payload.wellbeing_value ?? undefined,
    choices: payload.choices.map((c) => ({
      id: c.id,
      name: c.name,
      notes: c.notes || '',
      variables: c.variables,
    })),
  }
  const res = await client.post('/simulate', body)
  analytics.track('simulation_run', {
    n_simulations: payload.n_simulations,
    n_choices: payload.choices.length,
    // Demand signal for multi-criteria: how many runs weigh wellbeing at all,
    // and how many at a non-default rate (5000).
    has_wellbeing: payload.choices.some((c) => c.variables.some((v) => v.role === 'wellbeing')),
    wellbeing_value_custom: payload.wellbeing_value != null && payload.wellbeing_value !== 5000,
  })
  return res.data
}

export async function suggestVariables(
  decision_name: string,
  choice_name: string,
  choice_notes: string,
  horizon_years: number,
  base_currency: string = 'EUR',
): Promise<SuggestedVariable[]> {
  const res = await client.post('/agent/suggest-variables', {
    decision_name,
    choice_name,
    choice_notes,
    horizon_years,
    base_currency,
  })
  return res.data.variables
}

export async function synthesizeAgent(
  decision_name: string,
  responses: Array<{ razor: string; question: string; answer: any }>,
): Promise<AgentSynthesis> {
  const res = await client.post('/agent/synthesize', { decision_name, responses })
  return res.data
}

export async function agentFollowups(
  decision_name: string,
  responses: Array<{ razor: string; question: string; answer: any }>,
): Promise<string[]> {
  const res = await client.post('/agent/followups', { decision_name, responses })
  return res.data.questions ?? []
}

export async function summarizeResults(
  decision_name: string,
  horizon_years: number,
  summary: any,
  choices_brief: any[],
): Promise<{ headline: string; explanation: string; recommendation: string }> {
  const res = await client.post('/agent/summarize-results', {
    decision_name,
    horizon_years,
    summary,
    choices_brief,
  })
  return res.data
}

/** One validated change in a coach proposal — names and `from` values are
 *  resolved server-side from the model snapshot, not taken from the LLM. */
export interface CoachProposalChange {
  choice_id: string
  choice_name: string
  variable_id: string
  variable_name: string
  param: string
  from: number
  to: number
  reason?: string | null
}

export interface CoachProposal {
  changes: CoachProposalChange[]
}

export async function chatWithAgent(
  session_id: string,
  message: string,
  context?: any,
  decision_id?: string,
): Promise<{ reply: string; proposal: CoachProposal | null }> {
  const res = await client.post('/agent/chat', { session_id, message, context, decision_id })
  return { reply: res.data.reply, proposal: res.data.proposal ?? null }
}

/**
 * Streaming variant — calls onChunk with the accumulated reply as SSE tokens
 * arrive, and resolves with the full text. Throws on transport or in-stream
 * errors; callers should fall back to chatWithAgent.
 * Uses fetch (axios can't read response streams); auth rides on cookies.
 */
export async function chatWithAgentStream(
  session_id: string,
  message: string,
  context: any,
  onChunk: (accumulated: string) => void,
  onProposal?: (proposal: CoachProposal) => void,
  decision_id?: string,
): Promise<string> {
  const res = await fetch(`${API}/agent/chat-stream`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id, message, context, decision_id }),
  })
  if (!res.ok || !res.body) throw new Error(`stream failed: ${res.status}`)

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let full = ''
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    // SSE events are separated by a blank line; keep any trailing partial.
    const events = buffer.split('\n\n')
    buffer = events.pop() ?? ''
    for (const evt of events) {
      const line = evt.split('\n').find((l) => l.startsWith('data: '))
      if (!line) continue
      const payload = line.slice(6)
      if (payload === '[DONE]') return full
      const obj = JSON.parse(payload)
      if (obj.error) throw new Error(obj.error)
      if (obj.t) {
        full += obj.t
        onChunk(full)
      }
      if (obj.proposal?.changes?.length) onProposal?.(obj.proposal)
    }
  }
  if (!full) throw new Error('stream ended with no content')
  return full
}

/** A single-variable swing that changes the recommended winner. `value` is
 *  the central distribution parameter (mu / p / …) at the flip threshold. */
export interface FlipInfo {
  param: string | null
  value: number | null
  direction: 'down' | 'up'
  to_winner: string
  at_fraction: number
}

export interface TornadoEntry {
  choice: string
  variable: string
  role: string
  base_npv: number
  low_npv: number
  high_npv: number
  impact: number
  flip?: FlipInfo | null
}

// Public homepage count: distinct decisions run in the last 7 days
// (backend/routes/usage.py). Anonymous runs included.
export async function getPublicUsage(): Promise<{ decisions_last_7_days: number; as_of: string }> {
  const res = await client.get('/public/usage', { timeout: 5000 })
  return res.data
}

export async function runSensitivity(payload: {
  decision_id: string
  decision_name: string
  horizon_years: number
  discount_rate: number
  risk_aversion: number
  wellbeing_value?: number | null
  n_simulations?: number
  seed?: number
  choices: Choice[]
  target_choice?: string
}): Promise<{
  base_winner: string
  target: string
  entries: TornadoEntry[]
  robust?: boolean | null
  /** Best non-winner, and the swings on its inputs that make it win. */
  runner_up?: string | null
  runner_up_flips?: TornadoEntry[]
}> {
  const body = {
    ...payload,
    wellbeing_value: payload.wellbeing_value ?? undefined,
    choices: payload.choices.map((c) => ({
      id: c.id,
      name: c.name,
      notes: c.notes || '',
      variables: c.variables,
    })),
  }
  const res = await client.post('/sensitivity', body)
  return res.data
}

export async function bayesUpdate(payload: {
  prior_mu: number
  prior_sigma: number
  observation: number
  obs_sigma?: number
}): Promise<{ posterior_mu: number; posterior_sigma: number; explanation: string }> {
  const res = await client.post('/bayes/update', payload)
  return res.data
}

export async function publishShare(payload: {
  decision_name: string
  horizon_years: number
  summary: any
  choices_brief: any[]
  ai_headline?: string
  ai_explanation?: string
  ai_recommendation?: string
  is_public?: boolean
  choices_full?: any[]
  forked_from?: string
}): Promise<{ share_id: string; url_path: string }> {
  const res = await client.post('/share/publish', payload)
  return res.data
}

export async function bumpForkCounter(share_id: string): Promise<void> {
  try {
    await client.post(`/share/${share_id}/fork-counter`)
  } catch {
    // best-effort, ignore
  }
}

export async function reportShare(
  share_id: string,
  reason?: string,
): Promise<{ ok: boolean; hidden: boolean }> {
  const res = await client.post(`/share/${share_id}/report`, { reason: reason || null })
  return res.data
}

export interface ShareForkSummary {
  share_id: string
  decision_name: string
  author_name?: string | null
  view_count: number
  fork_count: number
  is_public: boolean
  created_at: string
}

export async function getShareForks(share_id: string, onlyPublic = true): Promise<ShareForkSummary[]> {
  const res = await client.get(`/share/${share_id}/forks`, { params: { only_public: onlyPublic } })
  return res.data.items
}

export interface LineageNode {
  share_id: string
  decision_name: string
  author_name?: string | null
  is_public: boolean
  fork_count: number
  view_count: number
  winner?: string | null
  forked_from?: string | null
  created_at?: string | null
  children?: LineageNode[]
}

export interface LineageResponse {
  target: string
  ancestors: LineageNode[]
  node: LineageNode
  descendants: LineageNode[]
}

export async function getShareLineage(share_id: string, depth = 2): Promise<LineageResponse> {
  const res = await client.get(`/share/${share_id}/lineage`, { params: { depth } })
  return res.data
}

export interface ExploreItem {
  share_id: string
  decision_name: string
  horizon_years: number
  winner: string
  p_better: number
  npv_gap: number
  p_loss: number
  n_options: number
  view_count: number
  author_name?: string | null
  ai_hook?: string | null
  created_at: string
}

export async function fetchExplore(sort: 'trending' | 'recent' | 'popular' = 'trending', limit = 30): Promise<ExploreItem[]> {
  const res = await client.get('/explore', { params: { sort, limit } })
  return res.data.items
}

export type FeaturedCategory = 'naming' | 'careers' | 'moves' | 'education' | 'money'

export async function fetchFeaturedExplore(category: FeaturedCategory = 'naming', limit = 6): Promise<ExploreItem[]> {
  const res = await client.get('/explore/featured', { params: { category, limit } })
  return res.data.items
}

export async function getShare(share_id: string): Promise<any> {
  const res = await client.get(`/share/${share_id}`)
  return res.data
}

// =====================================================================
// Auth
// =====================================================================

export interface AuthUser {
  id: string
  email: string
  name?: string
  role: string
  is_premium: boolean
}

/** Admins always have full access regardless of their billing flag. */
export function hasPremiumAccess(user: AuthUser | null | undefined): boolean {
  if (!user) return false
  return user.is_premium || user.role === 'admin'
}

// ── Feedback ─────────────────────────────────────────────────────────────────

/** `source` tags where the prompt was shown (e.g. 'siq_result') for inbox
 * triage — the backend whitelists it, so an unrecognized value is dropped
 * server-side rather than rejected; omit for the ambient floating button. */
export async function submitFeedback(text: string, source?: string): Promise<void> {
  await client.post('/feedback', { text, source })
}

/** Email a Should-I-Quit lead their odds. `summary` carries only the derived,
 * non-salary result fields — never the raw inputs. */
export async function submitSiqBreakdown(
  email: string,
  summary: Record<string, number | string | null>,
): Promise<void> {
  await client.post('/siq/breakdown', { email, summary })
}

// ── Billing ──────────────────────────────────────────────────────────────────

/** Lifetime Premium checkout, or — with a decisionId — a decision pass for that one decision. */
export async function createCheckoutSession(
  decisionId?: string,
): Promise<{ url: string; already_premium?: boolean; already_unlocked?: boolean }> {
  const product = decisionId ? 'decision_pass' : 'premium'
  analytics.track('upgrade_clicked', { product })
  const res = await client.post('/billing/checkout', decisionId ? { decision_id: decisionId } : undefined)
  if (!res.data.already_premium && !res.data.already_unlocked) analytics.track('checkout_started', { product })
  return res.data
}

/** Whether the signed-in user bought a decision pass for this decision. */
export async function getDecisionUnlocked(decisionId: string): Promise<boolean> {
  const res = await client.get(`/billing/unlocks/${encodeURIComponent(decisionId)}`)
  return Boolean(res.data?.unlocked)
}

export async function getBillingStatus(): Promise<{ is_premium: boolean; payment_date: string | null }> {
  const res = await client.get('/billing/status')
  return res.data
}

export async function authMe(): Promise<AuthUser> {
  const res = await client.get('/auth/me')
  return res.data
}

export async function authLogin(email: string, password: string): Promise<AuthUser> {
  const res = await client.post('/auth/login', { email, password })
  return res.data
}

export async function authRegister(email: string, password: string, name?: string): Promise<AuthUser> {
  const res = await client.post('/auth/register', { email, password, name })
  return res.data
}

export async function authLogout(): Promise<void> {
  await client.post('/auth/logout')
}

export async function authApple(identityToken: string, name?: string): Promise<AuthUser> {
  const res = await client.post('/auth/apple', { identity_token: identityToken, name })
  return res.data
}

export async function authGoogle(idToken: string): Promise<AuthUser> {
  const res = await client.post('/auth/google', { id_token: idToken })
  return res.data
}

// =====================================================================
// Decision sync (localStorage → user account migration)
// =====================================================================

import type { Decision } from './types'

export interface DecisionSyncResult {
  imported_count: number
  server_decisions: Decision[]
  total_count: number
}

export async function syncDecisions(decisions: Decision[]): Promise<DecisionSyncResult> {
  const res = await client.post('/decisions/sync', { decisions })
  return res.data
}

export async function deleteUserDecision(decision_id: string): Promise<void> {
  try {
    await client.delete(`/decisions/${decision_id}`)
  } catch {
    // best-effort; local delete still happens
  }
}

// =====================================================================
// Password reset
// =====================================================================

export async function forgotPassword(email: string): Promise<{ ok: boolean; message: string }> {
  const res = await client.post('/auth/forgot-password', { email })
  return res.data
}

export async function resetPassword(token: string, new_password: string): Promise<{ ok: boolean; message: string }> {
  const res = await client.post('/auth/reset-password', { token, new_password })
  return res.data
}

// =====================================================================
// Email prefs
// =====================================================================

export async function getEmailPrefs(): Promise<{ wants_weekly_digest: boolean; wants_outcome_emails: boolean }> {
  const res = await client.get('/auth/email-prefs')
  return res.data
}

export async function setEmailPrefs(prefs: { wants_weekly_digest: boolean; wants_outcome_emails: boolean }): Promise<{ wants_weekly_digest: boolean; wants_outcome_emails: boolean }> {
  const res = await client.post('/auth/email-prefs', prefs)
  return res.data
}

export async function generateBlogPost(shareId: string): Promise<{ ghost_post_url: string; ghost_admin_url: string; title: string }> {
  const res = await client.post('/blog/generate-post', { share_id: shareId })
  return res.data
}

// =====================================================================
// User stats (Streaks & rolls)
// =====================================================================

export interface UserStats {
  total_decisions: number
  total_simulations: number
  weekly_streak: number
  longest_streak: number
  total_shares: number
  total_views: number
  most_forked: { share_id: string; decision_name: string; fork_count: number; view_count: number } | null
}

export interface MyPreferences {
  locale?: string | null
  tour_completed?: boolean | null
}

export async function getMyPreferences(): Promise<MyPreferences> {
  const res = await client.get('/me/preferences')
  return res.data
}

export async function updateMyPreferences(patch: Partial<MyPreferences>): Promise<MyPreferences> {
  const res = await client.patch('/me/preferences', patch)
  return res.data
}

// ── Account management ────────────────────────────────────────────────────────

export interface AccountInfo {
  id: string
  email: string
  name: string | null
  is_premium: boolean
  payment_date: string | null
}

export async function getAccount(): Promise<AccountInfo> {
  const res = await client.get('/me/account')
  return res.data
}

export async function updateName(name: string): Promise<AccountInfo> {
  const res = await client.patch('/me/account/name', { name })
  return res.data
}

export async function updatePassword(current_password: string, new_password: string): Promise<void> {
  await client.patch('/me/account/password', { current_password, new_password })
}

export async function updateEmail(new_email: string, current_password: string): Promise<AccountInfo> {
  const res = await client.patch('/me/account/email', { new_email, current_password })
  return res.data
}

export async function deleteAccount(current_password: string): Promise<void> {
  await client.delete('/me/account', { data: { current_password } })
}

export interface FxRatesResponse {
  base: string
  rates: Record<string, number>
  as_of: string
  source: string
}

export async function getFxRates(base: string = 'EUR'): Promise<FxRatesResponse> {
  const res = await client.get('/fx/rates', { params: { base } })
  return res.data
}

export async function getUserStats(): Promise<UserStats> {
  const res = await client.get('/me/stats')
  return res.data
}

// =====================================================================
// Compare AI verdict
// =====================================================================

export interface CompareDecisionBrief {
  name: string
  horizon_years: number
  summary: any
  choices_brief: any[]
}

export interface CompareVerdict {
  headline: string
  verdict: string
  exclusive_pick?: string | null
  complementary_note?: string | null
}

export async function compareVerdict(
  decision_a: CompareDecisionBrief,
  decision_b: CompareDecisionBrief,
  relationship: 'exclusive' | 'complementary' = 'exclusive',
): Promise<CompareVerdict> {
  const res = await client.post('/agent/compare-verdict', { decision_a, decision_b, relationship })
  return res.data
}

// =====================================================================
// Bayesian-update history (per variable, per user)
// =====================================================================

export interface BayesHistoryEntry {
  decision_id: string
  choice_id: string
  variable_id: string
  variable_name: string
  prior_mu: number
  prior_sigma: number
  observation: number
  posterior_mu: number
  posterior_sigma: number
  note?: string | null
  created_at: string
}

export async function recordBayesHistory(entry: Omit<BayesHistoryEntry, 'created_at'>): Promise<BayesHistoryEntry> {
  const res = await client.post(`/decisions/${entry.decision_id}/bayes-history`, entry)
  return res.data
}

export async function getBayesHistory(decision_id: string, variable_id?: string): Promise<BayesHistoryEntry[]> {
  const res = await client.get(`/decisions/${decision_id}/bayes-history`, {
    params: variable_id ? { variable_id } : {},
  })
  return res.data.items
}

export function formatApiError(detail: any): string {
  if (detail == null) return 'Something went wrong.'
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    return detail
      // Pydantic prefixes validator messages with "Value error, ".
      .map((e: any) => (e && typeof e.msg === 'string' ? e.msg.replace(/^Value error, /, '') : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ')
  }
  if (detail && typeof detail.msg === 'string') return detail.msg
  return String(detail)
}

// ---------------------------------------------------------------------------
// Admin API
// ---------------------------------------------------------------------------

export interface AdminStats {
  total_users: number
  premium_users: number
  conversion_rate: number
  total_revenue_eur: number
  simulations_today: number
  public_shares: number
  users_per_day: { date: string; count: number }[]
  sims_per_day:  { date: string; count: number }[]
}

export interface AdminUserRow {
  id: string
  email: string
  name?: string
  role: string
  is_premium: boolean
  created_at: string
  decision_count: number
  simulation_count: number
}

export interface AdminUsersResponse {
  users: AdminUserRow[]
  total: number
  page: number
  pages: number
}

export interface AdminUserDetail extends AdminUserRow {
  payments: { id: number; amount_eur: number; currency: string; status: string; date: string; session: string }[]
  shares:   { share_id: string; name: string; public: boolean; views: number; forks: number; date: string }[]
}

export interface AdminPayment {
  id: number
  user_id: string
  user_email: string
  amount_eur: number
  currency: string
  status: string
  created_at: string
  stripe_session_id: string
}

export async function getAdminStats(): Promise<AdminStats> {
  const res = await client.get('/admin/stats')
  return res.data
}

export async function listAdminUsers(params: {
  page?: number; limit?: number; search?: string; plan?: string; role?: string
}): Promise<AdminUsersResponse> {
  const res = await client.get('/admin/users', { params })
  return res.data
}

export async function getAdminUser(id: string): Promise<AdminUserDetail> {
  const res = await client.get(`/admin/users/${id}`)
  return res.data
}

export async function patchAdminUser(id: string, body: { is_premium?: boolean; role?: string }): Promise<void> {
  await client.patch(`/admin/users/${id}`, body)
}

export async function deleteAdminUser(id: string): Promise<void> {
  await client.delete(`/admin/users/${id}`)
}

export async function resetAdminUserPassword(id: string): Promise<void> {
  await client.post(`/admin/users/${id}/reset`)
}

export async function listAdminPayments(page = 1): Promise<{ payments: AdminPayment[]; total_revenue_eur: number; completed_count: number; pending_count: number }> {
  const res = await client.get('/admin/payments', { params: { page } })
  return res.data
}

export async function listAdminShares(params: {
  page?: number; public_only?: boolean; sort_by?: string
}): Promise<{ shares: any[]; total: number; page: number; pages: number }> {
  const res = await client.get('/admin/shares', { params })
  return res.data
}

export async function unpublishAdminShare(shareId: string): Promise<void> {
  await client.patch(`/admin/shares/${shareId}`)
}

export async function deleteAdminShare(shareId: string): Promise<void> {
  await client.delete(`/admin/shares/${shareId}`)
}

// ── AI decision intake ────────────────────────────────────────────────────────

export interface IntakeMessage {
  role: 'user' | 'assistant'
  text: string
}

export interface IntakeQuestion {
  type: 'question'
  question: string
  field?: string
}

export interface IntakeVariable {
  name: string
  role: string
  time: string
  units: string
  dist_kind: string
  dist_params: Record<string, number>
}

export interface IntakeSoftFactor {
  name: string
  category: string
  importance: number
  score: number
}

export interface IntakeChoice {
  name: string
  variables: IntakeVariable[]
  soft_factors?: IntakeSoftFactor[]
}

export interface IntakeDecision {
  name: string
  horizon_years: number
  discount_rate: number
  risk_aversion: number
  choices: IntakeChoice[]
}

export interface IntakeResult {
  type: 'decision'
  decision: IntakeDecision
  rationale: string
}

export async function runIntake(messages: IntakeMessage[]): Promise<IntakeQuestion | IntakeResult> {
  const res = await client.post('/agent/intake', { messages })
  return res.data
}

// ---------------------------------------------------------------------------
// Decision check-ins (profile-concept §5.3) — explicit per-decision opt-in
// ---------------------------------------------------------------------------

export type CheckinVerdict = 'better' | 'as_expected' | 'worse'

export interface CheckinExpected {
  winner: string
  p_better: number
  npv_gap: number
  options: string[]
}

export interface Checkin {
  decision_id: string
  decision_name: string
  due_at: string
  expected: CheckinExpected
  sent_at?: string | null
  chosen?: string | null
  verdict?: CheckinVerdict | null
  note?: string | null
  recorded_at?: string | null
}

export async function listCheckins(): Promise<Checkin[]> {
  const res = await client.get('/decisions/checkins')
  return res.data
}

export async function scheduleCheckin(
  decision_id: string,
  payload: { decision_name: string; due_in_days: 30 | 60 | 90 | 180; expected: CheckinExpected },
): Promise<Checkin> {
  const res = await client.put(`/decisions/${encodeURIComponent(decision_id)}/checkin`, payload)
  return res.data
}

export async function cancelCheckin(decision_id: string): Promise<void> {
  await client.delete(`/decisions/${encodeURIComponent(decision_id)}/checkin`)
}

// One-tap outcome links from the check-in email: no sign-in, the token in the
// link reaches only that check-in's outcome.
export interface CheckinLink {
  decision_name: string
  options: string[]
  winner?: string | null
  p_better?: number | null
  chosen?: string | null
  verdict?: CheckinVerdict | null
  recorded_at?: string | null
}

export async function getCheckinLink(token: string): Promise<CheckinLink> {
  const res = await client.get(`/decisions/checkin-link/${encodeURIComponent(token)}`)
  return res.data
}

export async function recordCheckinLinkOutcome(
  token: string,
  payload: { chosen: string; verdict: CheckinVerdict; note?: string },
): Promise<CheckinLink> {
  const res = await client.post(`/decisions/checkin-link/${encodeURIComponent(token)}/outcome`, payload)
  return res.data
}

export async function recordCheckinOutcome(
  decision_id: string,
  payload: { chosen: string; verdict: CheckinVerdict; note?: string },
): Promise<Checkin> {
  const res = await client.post(`/decisions/${encodeURIComponent(decision_id)}/checkin/outcome`, payload)
  return res.data
}
